import { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { toast } from "sonner";
import { load } from "@cashfreepayments/cashfree-js";
import { LockKey, PenNib } from "@phosphor-icons/react";
import { api, errorMessage, inr, INDIAN_STATES, formatLabel } from "@/lib/api";
import { useCart } from "@/context/CartContext";
import { useAuth } from "@/context/AuthContext";
import { TrustBadges } from "@/components/store/TrustBadges";

const Field = ({ label, children }) => (
  <label className="block text-sm">
    <span className="mb-1.5 block font-medium">{label}</span>
    {children}
  </label>
);
const inputCls = "h-11 w-full rounded-xl border border-input bg-white px-3 text-sm outline-none ring-primary transition-shadow focus:ring-2";

export default function Checkout() {
  const { items, coupon, clear } = useCart();
  const { user } = useAuth();
  const navigate = useNavigate();
  const [quote, setQuote] = useState(null);
  const [busy, setBusy] = useState(false);
  const [form, setForm] = useState({ customer_name: user?.name || "", customer_phone: "", billing_state: "", line1: "", line2: "", city: "", state: "", pincode: "" });
  const [dedications, setDedications] = useState({});
  const set = (k) => (e) => setForm((f) => ({ ...f, [k]: e.target.value }));

  useEffect(() => {
    if (!items.length) { navigate("/cart"); return; }
    api.post("/cart/quote", { items: items.map(({ book_id, format, qty }) => ({ book_id, format, qty })), coupon_code: coupon || null })
      .then(({ data }) => setQuote(data))
      .catch((e) => { toast.error(errorMessage(e)); navigate("/cart"); });
  }, [items, coupon, navigate]);

  const pay = async (e) => {
    e.preventDefault();
    setBusy(true);
    try {
      const payload = {
        items: items.map(({ book_id, format, qty }) => ({ book_id, format, qty })),
        coupon_code: coupon || null,
        customer_name: form.customer_name,
        customer_phone: form.customer_phone.replace(/\s/g, ""),
        billing_state: quote.has_physical ? form.state : form.billing_state,
        shipping_address: quote.has_physical ? { line1: form.line1, line2: form.line2, city: form.city, state: form.state, pincode: form.pincode } : null,
        dedications,
      };
      const { data } = await api.post("/checkout", payload);
      clear();
      if (!data.payment_session_id) { navigate(`/payment/result?order_id=${data.order_id}`); return; }
      const cashfree = await load({ mode: data.cashfree_env === "production" ? "production" : "sandbox" });
      await cashfree.checkout({ paymentSessionId: data.payment_session_id, redirectTarget: "_self" });
    } catch (err) {
      toast.error(errorMessage(err));
      setBusy(false);
    }
  };

  if (!quote) return <div className="mx-auto max-w-6xl px-5 py-24 text-sm text-muted-foreground">Preparing checkout…</div>;

  return (
    <div className="mx-auto max-w-6xl px-5 py-12 sm:px-8 md:py-20" data-testid="checkout-page">
      <p className="eyebrow">Checkout</p>
      <h1 className="mt-3 text-4xl font-medium tracking-tight">Almost yours.</h1>
      <form onSubmit={pay} className="mt-12 grid gap-12 md:grid-cols-12">
        <div className="space-y-8 md:col-span-7">
          <section className="rounded-3xl bg-white p-6 ring-1 ring-border/70">
            <h2 className="font-display text-lg font-medium">Contact</h2>
            <div className="mt-5 grid gap-4 sm:grid-cols-2">
              <Field label="Full name"><input required value={form.customer_name} onChange={set("customer_name")} className={inputCls} data-testid="checkout-name-input" /></Field>
              <Field label="Mobile number"><input required pattern="^\+?[0-9 ]{10,15}$" placeholder="98765 43210" value={form.customer_phone} onChange={set("customer_phone")} className={inputCls} data-testid="checkout-phone-input" /></Field>
              <Field label="Email"><input disabled value={user?.email} className={`${inputCls} bg-muted text-muted-foreground`} data-testid="checkout-email-input" /></Field>
              {!quote.has_physical && (
                <Field label="State (for GST invoice)">
                  <select required value={form.billing_state} onChange={set("billing_state")} className={inputCls} data-testid="checkout-billing-state-select">
                    <option value="">Select state</option>
                    {INDIAN_STATES.map((s) => <option key={s}>{s}</option>)}
                  </select>
                </Field>
              )}
            </div>
          </section>
          {quote.has_physical && (
            <section className="rounded-3xl bg-white p-6 ring-1 ring-border/70">
              <h2 className="font-display text-lg font-medium">Shipping address</h2>
              <div className="mt-5 grid gap-4 sm:grid-cols-2">
                <div className="sm:col-span-2"><Field label="Address line 1"><input required value={form.line1} onChange={set("line1")} className={inputCls} data-testid="checkout-address1-input" /></Field></div>
                <div className="sm:col-span-2"><Field label="Address line 2 (optional)"><input value={form.line2} onChange={set("line2")} className={inputCls} data-testid="checkout-address2-input" /></Field></div>
                <Field label="City"><input required value={form.city} onChange={set("city")} className={inputCls} data-testid="checkout-city-input" /></Field>
                <Field label="PIN code"><input required pattern="^\d{6}$" value={form.pincode} onChange={set("pincode")} className={inputCls} data-testid="checkout-pincode-input" /></Field>
                <Field label="State">
                  <select required value={form.state} onChange={set("state")} className={inputCls} data-testid="checkout-state-select">
                    <option value="">Select state</option>
                    {INDIAN_STATES.map((s) => <option key={s}>{s}</option>)}
                  </select>
                </Field>
              </div>
            </section>
          )}
          {quote.has_physical && (
            <section className="rounded-3xl bg-white p-6 ring-1 ring-border/70" data-testid="bookplate-section">
              <div className="flex items-start gap-3">
                <PenNib weight="duotone" size={26} className="mt-0.5 shrink-0 text-primary" />
                <div>
                  <h2 className="font-display text-lg font-medium">Signed bookplate <span className="ml-1 rounded-full bg-muted px-2 py-0.5 text-[11px] font-semibold text-muted-foreground">free</span></h2>
                  <p className="mt-1 text-sm text-muted-foreground">Want it signed? Tell me who it's for and I'll hand-write a short dedication inside the cover. Leave blank for an unsigned copy.</p>
                </div>
              </div>
              <div className="mt-5 space-y-4">
                {quote.items.filter((it) => it.format !== "ebook").map((it) => (
                  <label key={it.book_id} className="block text-sm">
                    <span className="mb-1.5 flex justify-between font-medium"><span>{it.title}</span><span className="text-xs text-muted-foreground">{(dedications[it.book_id] || "").length}/120</span></span>
                    <input maxLength={120} placeholder='e.g. "For Priya — may the monsoon always find you reading"' value={dedications[it.book_id] || ""} onChange={(e) => setDedications({ ...dedications, [it.book_id]: e.target.value })} className={inputCls} data-testid={`dedication-input-${it.book_id}`} />
                  </label>
                ))}
              </div>
            </section>
          )}
          <TrustBadges compact />
        </div>
        <aside className="md:col-span-5">
          <div className="sticky top-24 rounded-3xl bg-white p-6 ring-1 ring-border/70" data-testid="checkout-summary">
            <h2 className="font-display text-lg font-medium">Order summary</h2>
            <ul className="mt-4 divide-y divide-border text-sm">
              {quote.items.map((it) => (
                <li key={it.book_id + it.format} className="flex items-center gap-3 py-3">
                  <img src={it.cover_url} alt="" className="h-14 w-10 rounded object-cover" />
                  <div className="flex-1"><p className="font-medium leading-snug">{it.title}</p><p className="text-xs text-muted-foreground">{it.format === "ebook" ? "Ebook PDF" : `${formatLabel(it.format)} × ${it.qty}`}</p></div>
                  <span>{inr(it.line_total)}</span>
                </li>
              ))}
            </ul>
            <dl className="mt-4 space-y-2 border-t border-border pt-4 text-sm">
              <div className="flex justify-between"><dt className="text-muted-foreground">Subtotal</dt><dd>{inr(quote.subtotal)}</dd></div>
              {quote.discount > 0 && <div className="flex justify-between text-accent"><dt>Discount ({quote.coupon_code})</dt><dd>− {inr(quote.discount)}</dd></div>}
              {quote.has_physical && <div className="flex justify-between"><dt className="text-muted-foreground">Shipping</dt><dd>{quote.shipping === 0 ? "Free" : inr(quote.shipping)}</dd></div>}
              <div className="flex justify-between text-xs text-muted-foreground"><dt>GST included</dt><dd>{inr(quote.tax)}</dd></div>
              <div className="flex justify-between border-t border-border pt-3 text-lg font-semibold"><dt>Total</dt><dd data-testid="checkout-total">{inr(quote.total)}</dd></div>
            </dl>
            <button type="submit" disabled={busy} className="mt-6 flex w-full items-center justify-center gap-2 rounded-full bg-primary py-4 text-sm font-semibold text-white transition-[transform,background-color] hover:-translate-y-0.5 hover:bg-[#A94D31] disabled:opacity-60" data-testid="pay-button">
              <LockKey weight="duotone" size={18} /> {busy ? "Opening secure checkout…" : `Pay ${inr(quote.total)} securely`}
            </button>
            <p className="mt-3 text-center text-[11px] leading-relaxed text-muted-foreground">
              You'll be taken to Cashfree to pay by UPI, card or netbanking. {quote.has_ebook && "Ebooks unlock the moment payment is confirmed."} {quote.has_physical && "Print copies dispatch within 2–3 working days."} All sales are final.
            </p>
          </div>
        </aside>
      </form>
    </div>
  );
}
